import { getFeatureFlagConfig } from 'src/utils/RemoteConfig';

import {
  getCookie,
  getLocalStorage,
  getSessionStorage
} from './browserStorageHandler';
import { DynamicObject } from './commonTypes';
import {
  RENEW_TOKEN_URL,
  SINGLEPLAY_URL,
  ZEE5_BASE_ADDRESS,
  EMAIL_LINK_URL,
  FB_LINK_URL,
  GOOGLE_LINK_URL,
  MOBILE_LINK_URL,
  TRUECALLER_SDK,
  TWITTER_LINK_URL,
  GWAPI_UPNEXT_URL
} from './constants/ApiConstants';
import { PARTNER } from './constants/cookieConstants';
import {
  USER_ID,
  GUEST_TOKEN,
  IS_TELCO_USER,
  UTM_SOURCE,
  USER_TYPE,
  PARTNER_NAME,
  DATA_COLLECTION_CONST,
  GDPRAPPIES,
  TCSTRING,
  REFRESH_TOKEN
} from './constants/localStorageConstants';
import { PartnerPlatforms, LINK_TYPE } from './constants/stringConstants';
import {
  INDIAN_LANGUAGES_SHORT_NAME,
  trueCallerProps,
  TRUECALLER_KEY,
  TRUECALLER_PARTNER,
  FeatureFlagConstants
} from './constants/utilConstants';
import { getGuestUserToken, getPlatformName } from './UtilityMethods';

const DEFAULT_SPAPI_VERSION = process.env.SPAPI_VERSION || '13';
const FALLBACK_SPAPI_VERSION = '12';

export function getSpapiVersion(remoteConfig: any): string {
  const gatingConfig = getFeatureFlagConfig(
    remoteConfig,
    FeatureFlagConstants.FEATURE_GATING
  );
  const isGatingEnabled = gatingConfig?.enabled ?? false;

  return isGatingEnabled ? DEFAULT_SPAPI_VERSION : FALLBACK_SPAPI_VERSION;
}

// The below function is used to remove undefined, null and empty array paramaters

export const filterParams = (params: DynamicObject) => {
  Object.keys(params).forEach((key) => {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key] === ''
    ) {
      delete params[key];
    } else if (Array.isArray(params[key]) && params[key].length === 0) {
      delete params[key];
    }
  });
};

// The below function appends query params
export const appendQueryParams = (
  url: string | URL,
  params: DynamicObject,
  useURLSearchParams = false,
  hyphenConversion = false
) => {
  if (!useURLSearchParams) {
    url += '?';
    Object.keys(params).map((key, index) => {
      if (index !== 0) {
        url += '&';
      }
      if (hyphenConversion) {
        const keyName = key.replace(/_/g, '-');
        url += `${keyName}=${params[key]}`;
      } else {
        url += `${key}=${params[key]}`;
      }
      return null;
    });
  } else {
    // use url search params. Special characters will be encoded
    url = new URL(url);
    url.search = new URLSearchParams(params).toString();
    url = url.href;
  }
  return url;
};

export function buildUrlWithQueryParams(url: string, parameters: object) {
  let qs = '';
  for (const key in parameters) {
    // if (parameters.hasOwnProperty(key)) {
    if (Object.prototype.hasOwnProperty.call(parameters, key)) {
      const value = parameters[key as keyof object];
      qs += value
        ? encodeURIComponent(key) +
          '=' +
          encodeURIComponent(value)?.replace(/%2C/g, ',') +
          '&'
        : '';
    }
  }
  if (qs.length > 0) {
    qs = qs.substring(0, qs.length - 1); // chop off last "&"
    url = url + '?' + qs;
  }

  return url;
}

export const getPlatformTokenUrl = (url: string) => {
  let platformToken = url ? `${url}?platform_name=web_app` : `web_app`;
  if (typeof window !== 'undefined') {
    const partnerName = getCookie(PARTNER) || '';
    if (partnerName === PartnerPlatforms.SAMSUNG_MY_GALAXY_APP) {
      if (partnerName === 'samsungmygalaxyapp') {
        if (platformToken.includes('web_app')) {
          return (platformToken = platformToken.replace(
            'web_app',
            'samsung_app'
          ));
        }
      }
    }
  }
  return platformToken;
};

// URL GENERATOR FOR THE SINGLE PLAY API with ads params
export const singlePlayApiUrlGenerator = async (
  obj: any,
  remoteConfig: any
) => {
  let src = `${SINGLEPLAY_URL}`;
  const appVersion = process.env.APP_VERSION;
  const descriptionUrl = `${ZEE5_BASE_ADDRESS}${window.location.pathname}`;
  const guestToken =
    getLocalStorage(GUEST_TOKEN) || (await getGuestUserToken());
  const userId = getLocalStorage(USER_ID);
  const platformName = getPlatformName(true);
  const userType = getLocalStorage(USER_TYPE) || 'guest';
  const adUrlParams: any = window.location.search.substring(1);
  const utmpageurl_latest = descriptionUrl.includes('/latest1') ? 1 : undefined;
  const isTelcoUser = getLocalStorage(IS_TELCO_USER);
  const spapiVersion = getSpapiVersion(remoteConfig);
  let partnerName = getCookie(PARTNER) as string;
  const utmSource = getSessionStorage(UTM_SOURCE);
  partnerName =
    partnerName === PartnerPlatforms.SAMSUNG_MY_GALAXY_APP
      ? (getCookie(PARTNER) as string)
      : '';
  if (!partnerName && isTelcoUser && obj.user) {
    partnerName = obj.user;
  } else if (!partnerName && isTelcoUser) {
    partnerName = getLocalStorage(PARTNER_NAME);
  }
  const dataCollectionData =
    getLocalStorage(DATA_COLLECTION_CONST) &&
    JSON.parse(getLocalStorage(DATA_COLLECTION_CONST));
  const ageRage =
    dataCollectionData &&
    dataCollectionData.ageJson &&
    dataCollectionData.ageJson.age_range;
  const genderData =
    dataCollectionData &&
    dataCollectionData.genderJson &&
    dataCollectionData.genderJson.gender;
  const gender = genderData || obj.gender;
  src += obj.isChannel ? `?channel_id=${obj.id}` : `?content_id=${obj.id}`;
  src += obj.isEpisodeConsumptionPage ? `&show_id=${obj.showId}` : '';
  src += obj.isLatestRoute ? '&is_latest=1' : '';
  src += obj.isLatest1Route ? '&is_marketing=1' : '';
  // TO be checked when api is ready device_id = guestToken (premium content spapi is failing)
  src += `&device_id=${guestToken}&platform_name=${platformName}&translation=${obj.translation}`;
  src += `&user_language=${obj.languages}&country=${obj.country}&state=${obj.region}&app_version=${appVersion}`;
  src += `&user_type=${userType}`;
  src += obj.isParentalControlSet
    ? `&check_parental_control=${obj.isParentalControlSet}&current_parental_control=${obj.parentalControlValue}`
    : `&check_parental_control=false`;
  src += gender ? `&gender=${gender}` : '';
  src += getLocalStorage('prefferedAudioStream')
    ? `&preferred_audio_language=${getLocalStorage('prefferedAudioStream')}`
    : '';
  src += obj.age ? `&age=${obj.age}` : '';
  src += ageRage ? `&age_group=${ageRage}` : '';
  if (adUrlParams.utm_source) {
    src += `&utm_source=${adUrlParams.utm_source}`;
  } else if (utmSource) {
    // utm_source must pass if it available in session storage
    src += `&utm_source=${utmSource}`;
  }
  src += adUrlParams.utm_medium ? `&utm_medium=${adUrlParams.utm_medium}` : '';
  src += adUrlParams.utm_campaign
    ? `&utm_campaign=${adUrlParams.utm_campaign}`
    : '';
  src += adUrlParams.utm_term ? `&utm_term=${adUrlParams.utm_term}` : '';
  src += adUrlParams.utm_content
    ? `&utm_content=${adUrlParams.utm_content}`
    : '';
  src += adUrlParams.dekey ? `&dekey=${adUrlParams.dekey}` : '';
  src += utmpageurl_latest ? `&utmpageurl_latest=${utmpageurl_latest}` : '';
  src += partnerName ? `&bd_source=${partnerName}` : '';
  src += userId ? `&uid=${userId}` : '';
  src += guestToken ? `&ppid=${guestToken}` : '';
  src += `&version=${spapiVersion}`;
  if (
    platformName === 'mobile_web' &&
    window.navigator.userAgent.match(/iPhone|iPad/i)
  ) {
    src = `${src}&os=ios`;
  } else if (
    platformName === 'mobile_web' &&
    window.navigator.userAgent.match(/Android/i)
  ) {
    src = `${src}&os=android`;
  }

  // one trust global code
  if (obj.country !== 'IN') {
    const consent =
      window && window.OnetrustActiveGroups
        ? filterConsent(window.OnetrustActiveGroups)
        : '';
    const gdprApplies = getLocalStorage(GDPRAPPIES) || '';
    const tcsString = getLocalStorage(TCSTRING) || '';
    src += `&zgdpr=${consent}&zcnstdt=${tcsString}&znpa=${gdprApplies}`;
  }
  return encodeURI(src);
};

// watchHistory

export const getWatchHistoryUrl = (userApiParam: any) => {
  const url = `${userApiParam}/api/v1/watchhistory`;
  return url;
};

// Upnext Movie url generator

// export const gwapiUpNextUrlGenerator = (params: any) => {
//   let url;
//   if (params.type === 'movie') {
//     url = `${GWAPI_UPNEXT_URL}/${params.type}/${params.asset_id}?translation=${params.translation}&languages=${params.languages}&country=${params.country}`;
//   } else {
//     url = `${GWAPI_UPNEXT_URL}/${params.type}/${params.series_id}/${params.asset_id}?translation=${params.translation}&languages=${params.languages}&season_id=${params.season_id}&country=${params.country}`;
//   }
//   if (params?.pc_age) {
//     url += `&pc_age=${params.pc_age}`;
//   }
//   return url;
// };

export const refreshTokenUrlGenerator = () => {
  const refreshToken = getLocalStorage(REFRESH_TOKEN);
  const url = `${RENEW_TOKEN_URL}?refresh_token=${refreshToken}`;
  return url;
};

export const filterConsent = (consent: string) => {
  const mainConsent = consent.replace(/"/g, '');
  const removecommaAtFirst =
    mainConsent.charAt(0) === ',' ? mainConsent.substring(1) : mainConsent;
  const removecommaAtEnd = removecommaAtFirst.endsWith(',')
    ? removecommaAtFirst.substring(0, removecommaAtFirst.length - 1)
    : removecommaAtFirst;
  return removecommaAtEnd;
};
export const trueCallerUrl = () => {
  const params = {
    ...trueCallerProps,
    requestNonce: getLocalStorage(GUEST_TOKEN),
    partnerKey: TRUECALLER_KEY,
    partnerName: TRUECALLER_PARTNER,
    lang: INDIAN_LANGUAGES_SHORT_NAME.english,
    privacyUrl: 'https://www.zee5.com/privacypolicy',
    termsUrl: 'https://www.zee5.com/termsofuse'
  };
  filterParams(params);
  return appendQueryParams(TRUECALLER_SDK, params);
};

// Linking URL generator for onboarding journey
export const linkingUrlGenerator = (linkType: string) => {
  let url;
  const { GOOGLE_LINK, FACEBOOK_LINK, TWITTER_LINK, EMAIL_LINK, MOBILE_LINK } =
    LINK_TYPE;

  switch (linkType) {
    case GOOGLE_LINK:
      url = GOOGLE_LINK_URL;
      break;
    case TWITTER_LINK:
      url = TWITTER_LINK_URL;
      break;
    case FACEBOOK_LINK:
      url = FB_LINK_URL;
      break;
    case EMAIL_LINK:
      url = EMAIL_LINK_URL;
      break;
    case MOBILE_LINK:
      url = MOBILE_LINK_URL;
      break;
    default:
      url = EMAIL_LINK_URL;
      break;
  }
  return url;
};

// Upnext Movie url generator

export const gwapiUpNextUrlGenerator = (params: DynamicObject) => {
  let url;
  if (params.type === 'movie') {
    url = buildUrlWithQueryParams(
      `${GWAPI_UPNEXT_URL}/${params.type}/${params.asset_id}`,
      {
        translation: params.translation,
        languages: params.languages,
        country: params.country
      }
    );
  } else {
    url = buildUrlWithQueryParams(
      `${GWAPI_UPNEXT_URL}/${params.type}/${params.series_id}/${params.asset_id}`,
      {
        translation: params.translation,
        languages: params.languages,
        season_id: params.season_id,
        country: params.country
      }
    );
  }

  if (params?.pc_age) {
    url += `&pc_age=${params.pc_age}`;
  }
  return url;
};
